import RichText from '@eneco/components/RichText/RichText';
import { Button, Grid, Heading, InputUsage, Stack } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { CustomFormField } from '@sitecore/types/PriceSimulation';
import { Controller, useFormContext } from 'react-hook-form';

import { ELECTRICITY_CONSUMPTION_OPTIONS, PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const ElectricityConsumptionStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const { trigger, watch, getValues } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const onNextClick = async () => {
    const isValid = await trigger('electricityConsumption');
    if (!isValid) {
      return;
    }
    trackEventBE('pbt_calculation_elec_cons', {
      status: 'click',
      data: getTrackableFormValues(getValues()),
    });
    nextMainStep();
  };

  const hasExclusiveNightMeter = watch('hasExclusiveNightMeter');
  const meterSubType = watch('meterSubTypeRegister');

  return (
    <>
      <Stack direction="column" gap="2">
        <Heading as="h1" size="M">
          {fields.electricityConsumptionStep.title.value}
        </Heading>
        <div>
          <RichText html={fields.electricityConsumptionStep.subTitle.value} />
        </div>
      </Stack>
      <Grid.Item alignSelf="stretch">
        {/* // Force re-mounting of MeterInput with key to clear values whenever hasExclNightMeter changes */}
        <Stack gap="4" key={`${meterSubType}-${hasExclusiveNightMeter}`}>
          {meterSubType?.toLowerCase() === 'single' ? (
            <MeterInput
              field={fields.electricityConsumptionStep.consumptionSingleMeterFormField}
              meterType={ELECTRICITY_CONSUMPTION_OPTIONS.SINGLE}
            />
          ) : (
            <>
              <div>
                <MeterInput
                  field={fields.electricityConsumptionStep.consumptionPeakHoursFormField}
                  meterType={ELECTRICITY_CONSUMPTION_OPTIONS.DAY}
                />
              </div>
              <div>
                <MeterInput
                  field={fields.electricityConsumptionStep.consumptionOffPeakHoursFormField}
                  meterType={ELECTRICITY_CONSUMPTION_OPTIONS.NIGHT}
                />
              </div>
            </>
          )}

          {hasExclusiveNightMeter && (
            <MeterInput
              field={fields.electricityConsumptionStep.consumptionExcludingNightHoursFormField}
              meterType={ELECTRICITY_CONSUMPTION_OPTIONS.EXCLUSIVE_NIGHT}
            />
          )}
        </Stack>
      </Grid.Item>

      <div>
        <RichText html={fields.electricityConsumptionStep.description.value} />
      </div>
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};
const MeterInput = ({
  field,
  meterType,
}: {
  field: CustomFormField;
  meterType: keyof NonNullable<PriceSimulationFormSchema['electricityConsumption']>;
}) => {
  const {
    formState: { errors },
    control,
  } = useFormContext<PriceSimulationFormSchema>();

  return (
    <Controller
      control={control}
      name={`electricityConsumption.${meterType}` as const}
      render={({ field: { onChange, value, ...fieldProps } }) => (
        <InputUsage
          label={field?.value?.label}
          placeholder={field?.value?.placeholder}
          hint={field?.value?.hint}
          type="energy"
          unit="kWh"
          value={value ?? ''}
          onChange={onChange}
          {...fieldProps}
          error={errors.electricityConsumption?.[meterType]?.message}
        />
      )}
    />
  );
};
